/**
 * Design Service Layer
 * 
 * SOLID Principles Applied:
 * - Single Responsibility: Only handles design-related API calls
 * - Dependency Inversion: Depends on axios abstraction, not concrete implementation
 * - Open/Closed: Easy to add new methods without modifying existing ones
 * 
 * KISS Principle: Each method does one thing clearly
 * 
 * This service abstracts all backend communication for designs.
 * Components should never call axios directly - they use this service.
 */

import axiosInstance from './axiosInstance';
import {
  IDesign,
  ICreateDesignDto,
  IUpdateDesignDto,
} from '../../types/design.types';
import { AxiosResponse } from 'axios';

const DEFAULT_MOCKUP_MAX_RETRIES = 3;
const DEFAULT_MOCKUP_RETRY_DELAY_MS = 600;

function isRetryableDefaultMockupError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status && status >= 500) return true;
  const message =
    error instanceof Error
      ? error.message
      : String((error as { message?: string })?.message ?? error);
  return /network|timeout|ECONNABORTED/i.test(message);
}

async function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Design Service Interface (for testing and extensibility)
 */
export interface IDesignService {
  createDesign(payload: ICreateDesignDto): Promise<IDesign>;
  getDesignById(id: string): Promise<IDesign>;
  getUserDesigns(): Promise<IDesign[]>;
  updateDesign(id: string, payload: IUpdateDesignDto): Promise<IDesign>;
  deleteDesign(id: string): Promise<void>;
  getProductsByDesign(designId: string): Promise<any[]>;
  assignDesignToProducts(designId: string, productIds: string[]): Promise<void>;
  assignDesignToProductsWithPricing(
    designId: string,
    products: Array<{ productId: string; sellerProfit: number }>
  ): Promise<void>;
  uploadCustomMockup(
    designId: string,
    productId: string,
    color: string,
    side: 'front' | 'back',
    file: File
  ): Promise<{ url: string; productId: string; color: string; side: 'front' | 'back' }>;
  deleteCustomMockup(
    designId: string,
    productId: string,
    color: string,
    side: 'front' | 'back'
  ): Promise<void>;
  setDefaultMockupSelection(
    designId: string,
    productId: string,
    color: string,
    side: 'front' | 'back',
    selection: 'system' | 'custom',
    // ✅ Enhanced: Optional fit and gender for per-combination default selection
    fit?: 'regular' | 'oversize',
    gender?: 'men' | 'women' | 'unisex'
  ): Promise<void>;
  generateMockupsForProducts(
    designId: string,
    productIds: string[]
  ): Promise<{
    message: string;
    designId: string;
    generatedColors: string[];
    frontMockupUrl?: string;
    backMockupUrl?: string;
    mockupUrlsByColor?: Record<string, { front?: string; back?: string }>;
  }>;
}

/**
 * Design Service Implementation
 */
class DesignService implements IDesignService {
  private readonly baseUrl = '/design';

  /**
   * Create a new design
   * POST /design
   */
  async createDesign(payload: ICreateDesignDto): Promise<IDesign> {
    const response: AxiosResponse<IDesign> = await axiosInstance.post(
      this.baseUrl,
      payload
    );
    return response.data;
  }

  /**
   * Get a single design by ID
   * GET /design/:id
   */
  async getDesignById(id: string): Promise<IDesign> {
    const response: AxiosResponse<IDesign> = await axiosInstance.get(
      `${this.baseUrl}/${id}`
    );
    return response.data;
  }

  /**
   * Get all designs for the current authenticated user
   * GET /design
   * @param params - Optional pagination parameters
   * @returns Designs array or paginated response
   */
  async getUserDesigns(params?: { page?: number; limit?: number; summary?: boolean }): Promise<IDesign[] | { designs: IDesign[]; pagination: any }> {
    const response: AxiosResponse<IDesign[] | { designs: IDesign[]; pagination: any }> = await axiosInstance.get(
      this.baseUrl,
      { params }
    );
    return response.data;
  }

  /**
   * Update an existing design
   * PUT /design/:id
   */
  async updateDesign(id: string, payload: IUpdateDesignDto): Promise<IDesign> {
    const response: AxiosResponse<IDesign> = await axiosInstance.put(
      `${this.baseUrl}/${id}`,
      payload
    );
    return response.data;
  }

  /**
   * Delete a design
   * DELETE /design/:id
   */
  async deleteDesign(id: string): Promise<void> {
    await axiosInstance.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Get products linked to a design
   * GET /design/:id/products
   */
  async getProductsByDesign(designId: string): Promise<any[]> {
    const response: AxiosResponse<any[]> = await axiosInstance.get(
      `${this.baseUrl}/${designId}/products`
    );
    return response.data;
  }

  /**
   * Assign design to products
   * POST /design/:designId/products
   */
  async assignDesignToProducts(
    designId: string,
    productIds: string[]
  ): Promise<void> {
    await axiosInstance.post(
      `${this.baseUrl}/${designId}/products`,
      productIds
    );
  }

  /**
   * Assign design to products with pricing (Hybrid Redbubble + Amazon model)
   * POST /design/:designId/products/pricing
   */
  async assignDesignToProductsWithPricing(
    designId: string,
    products: Array<{ productId: string; sellerProfit: number }>
  ): Promise<void> {
    await axiosInstance.post(
      `${this.baseUrl}/${designId}/products/pricing`,
      { products }
    );
  }

  /**
   * Get design-specific pricing for a product
   * GET /design/:designId/products/:productId/pricing
   */
  async getDesignProductPricing(
    designId: string,
    productId: string
  ): Promise<{
    finalPrice: number;
    sellerProfit: number;
    pricingTier: string;
    basePrice: number;
    minPrice: number | null;
    maxPrice: number | null;
  } | null> {
    try {
      const response = await axiosInstance.get(
        `${this.baseUrl}/${designId}/products/${productId}/pricing`
      );
      return response.data;
    } catch (error: any) {
      // Return null if pricing not found (design not assigned to product yet)
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get deleted designs (Recently Deleted)
   * GET /design/deleted/list
   */
  async getDeletedDesigns(): Promise<any[]> {
    const response = await axiosInstance.get(`${this.baseUrl}/deleted/list`);
    return response.data;
  }

  /**
   * Restore a deleted design
   * POST /design/:id/restore
   */
  async restoreDesign(id: string): Promise<IDesign> {
    const response: AxiosResponse<IDesign> = await axiosInstance.post(
      `${this.baseUrl}/${id}/restore`
    );
    return response.data;
  }

  /**
   * Claim a guest design after login
   * GET /design/claim/:id
   */
  async claimDesign(designId: string): Promise<any> {
    const response = await axiosInstance.get(
      `${this.baseUrl}/claim/${designId}`
    );
    return response.data;
  }

  /**
   * Upload a custom mockup image for a specific product + color + side
   * ✅ Enhanced: Now supports optional fits and gender
   * POST /design/:designId/custom-mockup
   */
  async uploadCustomMockup(
    designId: string,
    productId: string,
    color: string,
    side: 'front' | 'back',
    file: File,
    fit?: 'regular' | 'oversize',
    gender?: 'men' | 'women' | 'unisex'
  ): Promise<{ url: string; productId: string; color: string; side: 'front' | 'back'; fit?: string; gender?: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('productId', productId);
    formData.append('color', color);
    formData.append('side', side);
    // ✅ Enhanced: Append fits and gender if provided
    if (fit) {
      formData.append('fit', fit);
    }
    if (gender) {
      formData.append('gender', gender);
    }

    const response = await axiosInstance.post(
      `${this.baseUrl}/${designId}/custom-mockup`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  /**
   * Delete a custom mockup for a specific product + color + side
   * ✅ Enhanced: Now supports optional fits and gender
   * DELETE /design/:designId/custom-mockup/:productId/:color/:side?mockupUrl=...&fit=...&gender=...
   * @param mockupUrl Optional: specific URL to delete from array. If not provided, deletes all mockups for that side.
   * @param fit Optional: fit type ('regular' | 'oversize')
   * @param gender Optional: gender type ('men' | 'women' | 'unisex')
   */
  async deleteCustomMockup(
    designId: string,
    productId: string,
    color: string,
    side: 'front' | 'back',
    mockupUrl?: string,
    fit?: 'regular' | 'oversize',
    gender?: 'men' | 'women' | 'unisex'
  ): Promise<void> {
    const url = `${this.baseUrl}/${designId}/custom-mockup/${productId}/${color}/${side}`;
    const params: Record<string, string> = {};
    if (mockupUrl) params.mockupUrl = mockupUrl;
    if (fit) params.fit = fit;
    if (gender) params.gender = gender;
    const config = Object.keys(params).length > 0 ? { params } : undefined;
    await axiosInstance.delete(url, config);
  }

  async setDefaultMockupSelection(
    designId: string,
    productId: string,
    color: string,
    side: 'front' | 'back',
    selection: 'system' | 'custom' | string, // ✅ NEW: Can be 'system', 'custom', or a specific mockup URL
    // ✅ Enhanced: Optional fit and gender for per-combination default selection
    fit?: 'regular' | 'oversize',
    gender?: 'men' | 'women' | 'unisex'
  ): Promise<void> {
    const payload = {
      productId,
      color,
      side,
      selection,
      ...(fit && { fit }),
      ...(gender && { gender }),
    };

    let lastError: unknown;
    for (let attempt = 0; attempt < DEFAULT_MOCKUP_MAX_RETRIES; attempt++) {
      try {
        await axiosInstance.patch(
          `${this.baseUrl}/${designId}/default-mockup`,
          payload,
        );
        return;
      } catch (error) {
        lastError = error;
        if (
          !isRetryableDefaultMockupError(error) ||
          attempt >= DEFAULT_MOCKUP_MAX_RETRIES - 1
        ) {
          throw error;
        }
        await delay(DEFAULT_MOCKUP_RETRY_DELAY_MS * (attempt + 1));
      }
    }
    throw lastError;
  }

  /**
   * Generate mockups for a design based on selected product colors
   * POST /design/:designId/generate-mockups-for-products
   */
  async generateMockupsForProducts(
    designId: string,
    productIds: string[]
  ): Promise<{
    message: string;
    designId: string;
    generatedColors: string[];
    frontMockupUrl?: string;
    backMockupUrl?: string;
    mockupUrlsByColor?: Record<string, { front?: string; back?: string }>;
  }> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/${designId}/generate-mockups-for-products`,
      { productIds }
    );
    return response.data;
  }
}

// Export singleton instance (KISS - simple to use)
export const designService = new DesignService();

// Also export class for testing/mocking
export default DesignService;

